import { useQuery } from '@tanstack/react-query';
import { CheckIcon } from '@heroicons/react/24/outline';
import { usersService } from '@/services/users.service';
import { cn } from '@/utils/cn';

interface RoleSelectorProps {
  selectedRoleIds: string[];
  onChange: (roleIds: string[]) => void;
}

export default function RoleSelector({ selectedRoleIds, onChange }: RoleSelectorProps) {
  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => usersService.getRoles(),
  });

  const handleToggleRole = (roleId: string) => {
    if (selectedRoleIds.includes(roleId)) {
      onChange(selectedRoleIds.filter(id => id !== roleId));
    } else {
      onChange([...selectedRoleIds, roleId]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!roles || roles.length === 0) {
    return (
      <div className="text-sm text-secondary-500 dark:text-secondary-400 py-4">
        No roles available
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {roles.map((role) => {
        const isSelected = selectedRoleIds.includes(role.id);

        return (
          <label
            key={role.id}
            className={cn(
              'flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors',
              isSelected
                ? 'bg-primary-50 border-primary-500 dark:bg-primary-900/20 dark:border-primary-600'
                : 'bg-white border-secondary-200 hover:border-secondary-300 dark:bg-secondary-800 dark:border-secondary-700 dark:hover:border-secondary-600'
            )}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleToggleRole(role.id)}
              className="sr-only"
            />
            <div className={cn(
              'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mr-3 transition-colors',
              isSelected
                ? 'bg-primary-600 border-primary-600 dark:bg-primary-500 dark:border-primary-500'
                : 'bg-white border-secondary-300 dark:bg-secondary-700 dark:border-secondary-600'
            )}>
              {isSelected && <CheckIcon className="w-4 h-4 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className={cn(
                'font-medium text-sm',
                isSelected
                  ? 'text-primary-900 dark:text-primary-100'
                  : 'text-secondary-900 dark:text-white'
              )}>
                {usersService.getRoleDisplayName(role)}
              </div>
              {role.description && (
                <div className="text-xs text-secondary-600 dark:text-secondary-400 mt-0.5">
                  {role.description}
                </div>
              )}
              {role.isSystemRole && (
                <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-300 mt-1">
                  System Role
                </div>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
}
